<template>
  <div class="d-flex align-items-center">
    <b-button
      v-if="showButtons"
      :disabled="adjustStock <= min || disabled"
      variant="outline-primary"
      size="sm"
      class="d-flex align-items-center justify-content-center"
      style="width: 38px; height: 38px; min-width: 38px; min-height: 38px"
      @click="onMinus"
    >
      <i class="fas fa-minus"></i>
    </b-button>
    <div
      class="input-wrapper"
      :class="{ 'input-group': prefix || suffix }"
    >
      <div
        v-if="prefix"
        class="input-group-prepend"
      >
        <span
          class="input-group-text addon-text"
          :class="{ disabled: disabled }"
          >{{ prefix }}</span
        >
      </div>
      <div
        class="input-field-wrapper"
        :style="prefix || suffix ? 'flex: 1; min-width: 0;' : 'width: 100%;'"
      >
        <textarea
          ref="stockInput"
          inputmode="numeric"
          style="
            width: 100%;
            resize: none;
            overflow: hidden;
            word-break: break-all;
            min-height: 38px;
          "
          :value="displayValue"
          :placeholder="placeholder"
          :max="max"
          :min="min"
          @focus="onFocus"
          @input="onInputArea"
          @blur="onBlur($event)"
          @paste="onPaste($event)"
          :disabled="disabled"
          :class="{
            'form-control': true,
            'custom-textarea': true,
            'no-arrow': true,
            'text-left': textAlign === 'left',
            'text-center': textAlign === 'center',
            'text-right': textAlign === 'right',
            'is-invalid': invalid && !disabled,
            required: isRequired,
            'rounded-right-0': !!suffix,
            'rounded-left-0': !!prefix,
          }"
          @keydown="allowOnlyTwoDecimalNumber($event)"
          rows="1"
        ></textarea>
        <svg
          v-if="invalid && showErrorIcon"
          class="input-invalid-icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#dc3545"
          stroke-width="2"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
          />
          <line
            x1="12"
            y1="8"
            x2="12"
            y2="13"
          />
          <circle
            cx="12"
            cy="16.5"
            r="0.5"
            fill="#dc3545"
            stroke="none"
          />
        </svg>
      </div>
      <div
        v-if="suffix"
        class="input-group-append"
      >
        <span
          class="input-group-text addon-text"
          :class="{ disabled: disabled }"
          >{{ suffix }}</span
        >
      </div>
    </div>
    <b-button
      v-if="showButtons"
      :disabled="adjustStock >= max || disabled"
      variant="outline-primary"
      size="sm"
      class="d-flex align-items-center justify-content-center"
      style="width: 38px; height: 38px; min-width: 38px; min-height: 38px"
      @click="onPlus"
    >
      <i class="fas fa-plus"></i>
    </b-button>
  </div>
</template>

<script>
import "@/theme/modal/ProductAndStock/Product.scss";
import BigNumber from "bignumber.js";
import {
  validateTwoDecimal,
  formatThousandsSeparator,
} from "@/utils/numberValidation";

export default {
  name: "InputNumberField",
  components: {},
  props: {
    adjustStock: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 9999999999.99,
    },
    min: {
      type: Number,
      default: -9999999999.99,
    },
    onChange: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    showButtons: {
      type: Boolean,
      default: true,
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    textAlign: {
      type: String,
      default: "center",
      validator: value => ["left", "center", "right"].includes(value),
    },
    allowDecimal: {
      type: Boolean,
      default: true,
    },
    prefix: {
      type: String,
      default: "",
    },
    suffix: {
      type: String,
      default: "",
    },
    showErrorIcon: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isLoading: false,
      observer: null,
      isEditing: false,
      rawValue:
        this.adjustStock == null
          ? ""
          : new BigNumber(this.adjustStock).toString(),
    };
  },
  validations: {},
  computed: {
    displayValue() {
      if (this.isEditing) return String(this.rawValue ?? "");
      return formatThousandsSeparator(this.rawValue);
    },
  },
  mounted() {
    this.autoResize();
    if (window.IntersectionObserver && this.$refs.stockInput) {
      this.observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          this.autoResize();
        }
      });
      this.observer.observe(this.$refs.stockInput);
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    onFocus() {
      this.isEditing = true;
    },
    onInputArea(e) {
      const typed = e.target.value.replace(/,/g, "");
      // Replace "-0" with "-" so typing minus on a zero clears it
      if (typed === "-0" || typed === "-0.") {
        this.rawValue = "-";
        this.$refs.stockInput.value = "-";
        return;
      }
      this.onInputChange(typed);
    },
    autoResize() {
      this.$nextTick(() => {
        const target = this.$refs.stockInput;
        if (target) {
          target.style.height = "38px";
          if (target.scrollHeight > 38) {
            target.style.height = target.scrollHeight + 2 + "px";
          }
        }
      });
    },
    onInputChange(value) {
      const rawString = (value ?? "").toString().replace(/,/g, "");
      this.rawValue = rawString;
      const raw = rawString.trim();
      // Allow incomplete states while typing
      if (raw === "" || raw === "-" || raw === "." || raw === "-.") return;

      const bn = new BigNumber(raw);
      if (!bn.isFinite()) return;

      const result = this.allowDecimal
        ? bn.toNumber()
        : bn.integerValue(BigNumber.ROUND_DOWN).toNumber();
      this.onChange(result);
    },
    onMinus() {
      const current = new BigNumber(this.rawValue);
      if (!current.isFinite() || this.rawValue === "") {
        this.rawValue = "0";
        this.onChange(0);
      } else if (current.isGreaterThan(this.min)) {
        const result = current.minus(1);
        if (result.isLessThan(this.min)) {
          this.rawValue = new BigNumber(this.min).toFixed(2);
          return this.onChange(new BigNumber(this.rawValue));
        }
        if (!validateTwoDecimal(result)) {
          this.rawValue = result.toFixed(2);
        } else {
          this.rawValue = result.toNumber().toString();
        }
        this.onChange(new BigNumber(this.rawValue));
      }
    },
    onPlus() {
      const current = new BigNumber(this.rawValue);
      if (!current.isFinite() || this.rawValue === "") {
        this.rawValue = "1";
        this.onChange(1);
      } else if (current.isLessThan(this.max)) {
        const result = current.plus(1);
        if (result.isGreaterThan(this.max)) {
          this.rawValue = new BigNumber(this.max).toFixed(2);
          return this.onChange(new BigNumber(this.rawValue));
        }
        if (!validateTwoDecimal(result)) {
          this.rawValue = result.toFixed(2);
        } else {
          this.rawValue = result.toNumber().toString();
        }
        this.onChange(new BigNumber(this.rawValue));
      }
    },
    onBlur() {
      this.isEditing = false;
      let raw = (this.rawValue ?? "").toString().trim();

      if (raw === "" || raw === "-" || raw === ".") {
        this.rawValue = "";
        this.onChange(null);
        return;
      }

      //  -0, -0.0, 0.0, 00.00, 000, 000.000
      const isZeroLike = /^-?0*(?:\.0*)?$/.test(raw);
      if (isZeroLike) {
        this.rawValue = "0";
        this.onChange(new BigNumber(0));
        return;
      }

      const bn = new BigNumber(raw);
      if (!bn.isFinite()) {
        this.rawValue = "";
        this.onChange(null);
        return;
      }

      let settled = bn;
      if (bn.isLessThan(this.min)) {
        settled = new BigNumber(this.min);
      } else if (bn.isGreaterThan(this.max)) {
        settled = new BigNumber(this.max);
      } else if (!this.allowDecimal) {
        settled = bn.integerValue(BigNumber.ROUND_DOWN);
      } else if (!validateTwoDecimal(raw)) {
        settled = bn.decimalPlaces(2, BigNumber.ROUND_DOWN);
      }
      this.rawValue = settled.toString();
      this.onChange(settled);
    },
    allowOnlyTwoDecimalNumber(e) {
      if (e.ctrlKey || e.metaKey) {
        const k = (e.key || "").toLowerCase();
        const allowed = ["a", "c", "v", "x", "z", "y"];
        if (allowed.includes(k)) return;
      }
      if (e.shiftKey && e.key === "Insert") return;

      const allowedKeys = [
        "Backspace",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
      ];

      if (allowedKeys.includes(e.key)) return;

      const input = e.target;
      const value = input.value || "";
      const { selectionStart, selectionEnd } = input;

      const nextValue =
        value.slice(0, selectionStart) + e.key + value.slice(selectionEnd);
      const rawNextValue = nextValue.replace(/,/g, "");

      if (!this.isValueWithinRange(rawNextValue)) {
        e.preventDefault();
      }

      // Minus: only at the very start
      if (e.key === "-") {
        if (selectionStart !== 0) {
          e.preventDefault();
        }
        return;
      }

      // One decimal point only (block if decimal not allowed)
      if (e.key === ".") {
        if (!this.allowDecimal || value.includes(".")) {
          e.preventDefault();
        }
        return;
      }

      // Digits 0–9: allow only if result would have at most 2 decimal places
      if (/^\d$/.test(e.key)) {
        const newValue =
          value.slice(0, selectionStart) + e.key + value.slice(selectionEnd);
        const parts = newValue.split(".");
        if (parts.length === 2 && parts[1].length > 2) {
          e.preventDefault();
        }
        return;
      }
      e.preventDefault();
    },
    onPaste(e) {
      const raw = (
        (e.clipboardData || window.clipboardData)?.getData("text") || ""
      )
        .trim()
        .replace(/,/g, "");

      const pattern = this.allowDecimal ? /^-?\d*(?:\.\d*)?$/ : /^-?\d+$/;
      if (!raw || raw === "-" || raw === "." || !pattern.test(raw)) {
        e.preventDefault();
        return;
      }

      e.preventDefault();

      let bn = new BigNumber(raw);
      if (!bn.isFinite()) return;

      const minBN = new BigNumber(this.min ?? 0);
      const maxBN = new BigNumber(this.max ?? 0);

      if (bn.isLessThan(minBN) || bn.isGreaterThan(maxBN)) {
        e.preventDefault();
        return;
      }

      if (bn.isLessThan(minBN)) bn = minBN;
      if (bn.isGreaterThan(maxBN)) bn = maxBN;

      bn = this.allowDecimal
        ? bn.decimalPlaces(2, BigNumber.ROUND_DOWN)
        : bn.integerValue(BigNumber.ROUND_DOWN);

      this.value = bn.isZero() ? "0" : bn.toString();
      this.onChange(bn);
    },
    isValueWithinRange(rawValue) {
      if (
        rawValue === "" ||
        rawValue === "-" ||
        rawValue === "." ||
        rawValue === "-."
      ) {
        return true;
      }

      if (!/^-?\d*(?:\.\d*)?$/.test(rawValue)) {
        return false;
      }

      const bn = new BigNumber(rawValue);
      if (!bn.isFinite()) return false;

      const minBN = new BigNumber(this.min);
      const maxBN = new BigNumber(this.max);

      return bn.isGreaterThanOrEqualTo(minBN) && bn.isLessThanOrEqualTo(maxBN);
    },
  },
  watch: {
    rawValue() {
      this.autoResize();
    },
    adjustStock(newVal) {
      if (this.isEditing) return;
      if (newVal == null) {
        this.rawValue = "";
        return;
      }
      const result = new BigNumber(newVal);
      if (!validateTwoDecimal(result)) {
        this.rawValue = result.toFixed(2);
      } else {
        this.rawValue = result.toString();
      }
    },
  },
};
</script>

<style scoped>
.required {
  background-color: #ccecff6c;
}

.input-wrapper {
  position: relative;
  display: inline-flex;
  width: 100%;
}

.addon-text {
  max-width: 80px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
  display: block;
}

.addon-text.disabled {
  color: #c6c6c6;
}

.input-field-wrapper {
  position: relative;
}

.input-invalid-icon {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 1;
  width: 20px;
  height: 20px;
}

::v-deep .rounded-right-0 {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

::v-deep .rounded-left-0 {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

.no-arrow::-webkit-inner-spin-button,
.no-arrow::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.no-arrow {
  -moz-appearance: textfield;
}

button:disabled {
  background-color: white;
  border-color: #ebebeb;
  color: #ebebeb;
  cursor: not-allowed;
}

::v-deep .form-control.is-invalid,
::v-deep .was-validated .form-control:invalid {
  background-image: none !important;
  padding-right: 0.75rem !important;
}

::v-deep .form-control:disabled {
  background-color: #e8e8e8 !important;
  color: #c6c6c6 !important;
  border-color: #dee2e6 !important;
}

::v-deep .form-control:disabled::placeholder {
  color: #c6c6c6 !important;
}

.custom-textarea {
  line-height: 1.5;
  padding-top: 6px;
  padding-bottom: 6px;
}
</style>
